<template>
  <SpmOverlayPanel
    class-trigger="flex align-items-center spm-profile-trigger"
    class-header="flex align-items-center profile-info justify-content-left"
    class-content="flex align-items-center justify-content-center spm-overlay-panel-content"
    class-footer="flex align-items-center justify-content-center spm-overlay-panel-content"
  >
    <template #trigger>
      <div
        class="spm-profile-trigger__avatar"
        data-test-id="profile-btn"
      >
        <span class="initials">
          {{ initials }}
        </span>
      </div>
      <div class="spm-profile-trigger__info">
        <div class="spm-profile-trigger__role">
          {{ t(`user.types.${UserState.user.userType}`) }}
        </div>
        <div class="spm-profile-trigger__name">
          {{ UserState.user.firstName }} {{ UserState.user.lastName }}
        </div>
      </div>
      <i class="spm-profile-trigger__chevron fa-regular fa-angle-down" />
    </template>
    <template #header>
      <div
        class="avatar"
        data-test-id="profile-btn"
      >
        <span class="initials">
          {{ initials }}
        </span>
      </div>
      <div class="text ml-1">
        <div
          class="font-bold text-left"
        >
          {{ UserState.user.firstName }} {{ UserState.user.lastName }}
        </div>
        <div
          class="text-xs text-left  "
        >
          {{ UserState?.user.email }}
        </div>
      </div>
    </template>
    <SpmPanelMenu
      :items="profileMenuItems"
    />
    <template #footer>
      <SpmPanelMenu
        :items="logoutItem"
      />
    </template>
  </SpmOverlayPanel>
</template>

<script lang="ts">
import {
  computed, defineComponent, ref, Ref,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from '@/store';
import { PrimvueMenuModel } from '@/types';
import { findPermission, Logout, UserState } from '@/composables/User';
import { getErrorMessage, showToastError } from '@/helpers';
import { useRouter } from 'vue-router';
import SpmPanelMenu from '@/components/spm-primevue/SpmPanelMenu.vue';
import SpmOverlayPanel from '@/components/spm-primevue/SpmOverlayPanel.vue';

export default defineComponent({
  name: 'MyProfileMenu',
  components: {
    SpmOverlayPanel,
    SpmPanelMenu,
  },

  setup() {
    const { t } = useI18n();
    const store = useStore();
    const router = useRouter();
    // eslint-disable-next-line max-len
    const initials = computed(() => ((UserState.user?.firstName && UserState.user?.lastName) ? `${UserState.user.firstName.substring(0, 1)}${UserState.user.lastName.substring(0, 1)}` : ''));

    const logout = async () => {
      store.commit('general/showTheSpinner');
      try {
        await Logout();
      } catch (error) {
        await showToastError(getErrorMessage(error));
      } finally {
        await router.push({ name: 'login' });
        window.location.reload();
      }
    };

    const profileMenuItems: Ref<PrimvueMenuModel[]> = ref([
      {
        icon: 'fa-regular fa-user',
        label: t('profileMenu.myInformation'),
        to: { name: 'user.profile' },
      },
      {
        icon: 'fa-regular fa-chart-pie',
        label: t('profileMenu.statsListing'),
        to: { name: 'users.stats' },
      },
      {
        icon: 'fa-regular fa-users-gear',
        label: t('profileMenu.permissions'),
        to: { name: 'user.sub-user-list' },
        visible: () => findPermission('admin'),
      },
      // {
      //   icon: 'fa-regular fa-gear',
      //   label: t('profileMenu.manageMyAccount'),
      //   to: { name: 'user.manage-my-account' },
      // },
    ]);

    const logoutItem: Ref<PrimvueMenuModel[]> = ref([
      {
        label: t('profileMenu.logout'),
        icon: 'fa-regular fa-arrow-right-from-bracket',
        command: logout,
        class: 'data-test-logout-btn',
      },
    ]);

    return {
      t,
      UserState,
      profileMenuItems,
      logoutItem,
      initials,
    };
  },
});
</script>

<style lang="scss">
.spm-profile-trigger {
  cursor: pointer;
  transition: all 0.15s ease;
  padding: 0.3rem 0.625rem;
  border-radius: 10px;
  gap: 0.5rem;

  &:hover {
    background: #f5f6f8;
  }

  .spm-profile-trigger__avatar {
    border-radius: 50%;
    width: 2rem;
    height: 2rem;
    min-width: 2rem;
    background: linear-gradient(135deg, #94c840 0%, #7aae26 100%);
    color: #fff;
    font-size: 0.7rem;
    font-weight: 700;
    letter-spacing: 0.02em;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;

    .initials {
      line-height: 1;
    }
  }

  .spm-profile-trigger__info {
    display: flex;
    flex-direction: column;
    justify-content: center;
    text-align: left;
  }

  .spm-profile-trigger__role {
    color: #9ca3af;
    font-size: 0.6875rem;
    text-transform: capitalize;
    line-height: 1.3;
    white-space: nowrap;
  }

  .spm-profile-trigger__name {
    color: #374151;
    font-size: 0.8125rem;
    font-weight: 600;
    line-height: 1.3;
    white-space: nowrap;
  }

  .spm-profile-trigger__chevron {
    color: #9ca3af;
    font-size: 0.7rem;
    flex-shrink: 0;
  }
}
.profile-info {
  padding: 1rem 0.75rem;

  .avatar {
    position: relative;
    border-radius: 50%;
    width: 2.5rem;
    height: 2.5rem;
    background: linear-gradient(135deg, #94c840 0%, #7aae26 100%);
    color: #fff;
    font-size: 0.8rem;
    font-weight: 700;
    flex-shrink: 0;

    & .initials {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
    }
  }

  .text {
    :first-child {
      color: #1e1e1e;
      font-weight: 600;
    }

    color: #9ca3af;
    font-size: 0.8125rem;
  }
}
</style>
